import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useEffect } from 'react';

export function TermsOfService() {
  useEffect(() => {
    document.title = 'xReplAI - Terms of Service';
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1628] dark:bg-[#0A1628]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-24">
        <div className="prose prose-invert max-w-none">
          <Link to="/" className="text-[#60A5FA] hover:text-[#3B82F6] mb-8 inline-block">
            ← Back to Home
          </Link>
          
          <h1 className="text-4xl font-bold text-white mb-8">Terms of Service</h1>
          <p className="text-gray-400 mb-8">Last updated: February 4, 2026</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-300 mb-4">
              By accessing and using xRepl.ai ("Service"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p className="text-gray-300 mb-4">
              xRepl.ai is an AI-powered Chrome extension that helps users draft smart, personalized email replies for Gmail and LinkedIn. The Service uses artificial intelligence to analyze email content and generate appropriate responses.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-semibold text-white mb-3">3.1 Account Creation</h3>
            <p className="text-gray-300 mb-4">
              You must create an account to use certain features of the Service. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">3.2 Account Security</h3>
            <p className="text-gray-300 mb-4">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized access to or use of your account.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">3.3 Age Requirement</h3>
            <p className="text-gray-300 mb-4">
              You must be at least 13 years old to use the Service. If you are under 18, you must have permission from a parent or guardian.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Acceptable Use</h2>
            <p className="text-gray-300 mb-2">You agree NOT to use the Service to:</p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Violate any applicable laws or regulations</li>
              <li>Send spam, phishing emails, or malicious content</li>
              <li>Impersonate others or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to systems</li>
              <li>Use automated systems to access the Service excessively</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Remove or modify any proprietary notices</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Subscription and Payments</h2>
            <h3 className="text-xl font-semibold text-white mb-3">5.1 Subscription Plans</h3>
            <p className="text-gray-300 mb-4">
              The Service offers various subscription plans with different features and pricing. Current pricing is available on our website and may be changed with notice.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">5.2 Billing</h3>
            <p className="text-gray-300 mb-4">
              Subscriptions are billed in advance on a monthly or annual basis. You authorize us to charge your payment method for all fees incurred. All fees are non-refundable except as required by law.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">5.3 Cancellation</h3>
            <p className="text-gray-300 mb-4">
              You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. You will retain access to paid features until the end of your billing period.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">5.4 Free Trial</h3>
            <p className="text-gray-300 mb-4">
              We may offer a free trial period. At the end of the trial, your subscription will automatically convert to a paid subscription unless you cancel before the trial ends.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property</h2>
            <h3 className="text-xl font-semibold text-white mb-3">6.1 Our IP</h3>
            <p className="text-gray-300 mb-4">
              The Service, including its original content, features, and functionality, is owned by xRepl.ai and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">6.2 Your Content</h3>
            <p className="text-gray-300 mb-4">
              You retain ownership of email content you process through the Service. By using the Service, you grant us a limited license to process your content solely to provide the Service to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. AI-Generated Content</h2>
            <p className="text-gray-300 mb-4">
              Email responses generated by our AI are suggestions only. You are solely responsible for reviewing, editing, and deciding whether to send AI-generated content. We do not guarantee the accuracy, appropriateness, or quality of AI-generated content.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-gray-300 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-300 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, xRepl.ai SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. Indemnification</h2>
            <p className="text-gray-300 mb-4">
              You agree to indemnify and hold harmless xRepl.ai and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising out of your use of the Service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">11. Termination</h2>
            <p className="text-gray-300 mb-4">
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Service will cease immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to Terms</h2>
            <p className="text-gray-300 mb-4">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">13. Governing Law</h2>
            <p className="text-gray-300 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">14. Dispute Resolution</h2>
            <p className="text-gray-300 mb-4">
              Any dispute arising from these Terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association (AAA). You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Information</h2>
            <p className="text-gray-300 mb-4">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-300">
              Email: <a href="mailto:support@xrepl.ai" className="text-[#60A5FA] hover:text-[#3B82F6]">support@xrepl.ai</a><br />
              Website: <a href="https://www.xrepl.ai" className="text-[#60A5FA] hover:text-[#3B82F6]">www.xrepl.ai</a>
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}